import React, { useRef } from 'react'
import styled from '@emotion/styled'
import {
  TextArea,
  IconButton,
  Tooltip,
  Alert,
  Text,
  typography,
} from '@retailer-platform/shared-components/src/tds'
import { colors } from '@retailer-platform/shared-components/src/foundation'
import { ArrowUpIcon, EditIcon } from '@instacart/ids-core'
import { CloseIcon } from '@instacart/ids-core'
import { Role, FeedbackEnum } from '../../../__codegen__/api'
import { AiButtonSendMessage } from '../../../gin-and-tonic/common/ai-button-send-message/AiButtonSendMessage'
import { useDashMessages } from '../../../intl/intl.hooks'
import { trackEvent } from '../../events/trackEvent'
import { useCurrentAccountDetails } from '../../../api/current-account/currentAccount.hooks'
import { usePartnerId, useRetailerId } from '../../routing/params.hooks'
import AiGradients from '../../../gin-and-tonic/common/ai-button/AiGradients'
import { AnimatedSparkleIcon } from '../../../gin-and-tonic/common/ai-button/AnimatedSparkleIcon'
import { useAiAgent } from './AiAgentContext'
import { ChatMessage } from './ChatMessage'
import { CommentModal } from './CommentModal'
import { useCommentModal } from './CommentModalContext'
import { ConversationSelector } from './components/ConversationSelector'

interface ChatBarProps {}

const ChatBarContainer = styled.div<{ isOpen: boolean }>(() => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
  boxSizing: 'border-box',
  overflow: 'hidden',
}))

const ChatHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '56px',
  padding: '12px 20px',
  borderBottom: '1px solid #f0f0f0',
  background: '#fff',
})

const Group = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const MessagesPane = styled.div({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
})

const MessagesArea = styled.div({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '24px 20px 0 20px',
})

const InputArea = styled.form({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch',
  padding: '16px 20px',
  background: '#fff',
  gap: '12px',
})

const QuickActionArea = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
})

const QuickActionsLabel = styled.div({
  color: colors.GRAYSCALE.X70,
  ...typography.accentRegular,
  marginBottom: '8px',
})

const StyledTextArea = styled(TextArea)({
  flexGrow: 1,
  marginRight: '8px',
  marginBottom: '16px',
  backgroundColor: colors.GRAYSCALE.X10,
})

const ButtonContainer = styled.div({
  marginTop: '-58px',
  textAlign: 'right',
  marginRight: '8px',
})

const IconButtonInside = styled(IconButton)({
  padding: 4,
  minWidth: 32,
  minHeight: 32,
})

const LoadingContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  padding: '14px 14px',
  borderRadius: '16px 16px 16px 0px',
  backgroundColor: colors.GRAYSCALE.X0,
  color: colors.GRAYSCALE.X80,
  gap: '8px',
})

const EmptyStateContainer = styled.div({
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  color: colors.GRAYSCALE.X70,
  marginBottom: '32px',
  padding: 0,
})

const EmptyStateBottomContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '0 20px',
})

const GradientText = styled(Text)({
  ...typography.headline,
})

export const AiAgentChatBar: React.FC<ChatBarProps> = () => {
  const {
    isChatOpen,
    closeChat,
    loading,
    isAgentPowered,
    updateMessageFeedback,
    updateMessageComment,
    getRegisteredPrompts,
    clearConversation,
    clearConversationWithoutSaving,
    conversationHistory,
    currentConversationId,
    loadConversation,
    clearAllConversations,
    messages,
  } = useAiAgent()
  const [inputValue, setInputValue] = React.useState('')
  const messagesEndRef = React.useRef<null | HTMLDivElement>(null)
  const textFieldRef = React.useRef<HTMLTextAreaElement>(null)
  const { sendMessage } = useAiAgent()
  const { modalState, saveComment, setOnSaveComment, closeCommentModal } = useCommentModal()

  // Get user metadata for tracking
  const { account } = useCurrentAccountDetails()
  const partnerId = usePartnerId({ throwOnMissing: false })
  const retailerId = useRetailerId({ throwOnMissing: false })

  // Create a stable key for animation that only changes when we transition to empty state
  const isEmpty = messages.length === 0
  const animationKeyRef = useRef(`empty-${Date.now()}`)

  // Only update the key when transitioning to empty state
  if (isEmpty && !animationKeyRef.current.startsWith('empty-')) {
    animationKeyRef.current = `empty-${Date.now()}`
  } else if (!isEmpty) {
    animationKeyRef.current = 'not-empty'
  }

  // Set up the save comment callback
  React.useEffect(() => {
    setOnSaveComment(updateMessageComment)
  }, [setOnSaveComment, updateMessageComment])

  const intlMessages = useDashMessages({
    title: 'aiAgent.chatBar.title',
    newConversation: 'aiAgent.chatBar.newConversation',
    closeChat: 'aiAgent.chatBar.closeChat',
    emptyStateStartConversation: 'aiAgent.chatBar.emptyState.startConversation',
    notPoweredAlert: 'aiAgent.chatBar.notPowered.alert',
    quickActionsLabel: 'aiAgent.chatBar.quickActions.label',
    quickActionsWhatCanYouDo: 'aiAgent.chatBar.quickActions.whatCanYouDo',
    inputPlaceholder: 'aiAgent.chatBar.input.placeholder',
    inputAriaLabel: 'aiAgent.chatBar.input.ariaLabel',
    sendButtonAriaLabel: 'aiAgent.chatBar.sendButton.ariaLabel',
    thinking: 'aiAgent.chatBar.loading.thinking',
  })

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  React.useEffect(() => {
    scrollToBottom()
  }, [messages])

  React.useEffect(() => {
    if (isChatOpen) {
      textFieldRef.current?.focus()
    }
  }, [isChatOpen])

  const handleSendMessage = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault()
    if (inputValue.trim() && !loading) {
      sendMessage(inputValue)
      setInputValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleThumbsUp = async (messageIndex: number) => {
    await updateMessageFeedback(messageIndex, FeedbackEnum.Positive)
  }

  const handleThumbsDown = async (messageIndex: number) => {
    await updateMessageFeedback(messageIndex, FeedbackEnum.Negative)
  }

  const handleNewConversation = () => {
    // Track conversation clear
    trackEvent({
      id: 'aiAgent.chatBar.newConversation',
      description: 'User started new AI agent conversation',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        messageCount: messages.length,
        isAgentPowered,
      },
    })
    clearConversation()
  }

  const handleSelectConversation = (conversationId: string) => {
    // Track conversation selection
    trackEvent({
      id: 'aiAgent.chatBar.selectConversation',
      description: 'User selected AI agent conversation from history',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        selectedConversationId: conversationId,
        isAgentPowered,
      },
    })
    loadConversation(conversationId)
  }

  const handleClearAllConversations = () => {
    // Track clear all conversations
    trackEvent({
      id: 'aiAgent.chatBar.clearAllConversations',
      description: 'User cleared all AI agent conversations and started new one',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        isAgentPowered,
      },
    })

    // Clear all conversation history first
    clearAllConversations()

    // Then clear the current conversation WITHOUT saving it (since we just cleared all history)
    clearConversationWithoutSaving()
  }

  const handleCloseChat = () => {
    // Track chat close
    trackEvent({
      id: 'aiAgent.chatBar.closeChat',
      description: 'User closed AI agent chat',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        messageCount: messages.length,
        isAgentPowered,
      },
    })
    closeChat()
  }

  const registeredPrompts = getRegisteredPrompts()

  return (
    <>
      <ChatBarContainer isOpen={isChatOpen} data-testid="chat-bar">
        <ChatHeader>
          <Group>
            <ConversationSelector
              conversations={conversationHistory}
              currentConversationId={currentConversationId}
              onSelectConversation={handleSelectConversation}
              onClearAllConversations={handleClearAllConversations}
            />
            <Tooltip label={intlMessages.newConversation}>
              <IconButton
                icon={EditIcon}
                variant="tertiary"
                onClick={handleNewConversation}
                aria-label={intlMessages.newConversation}
              />
            </Tooltip>
          </Group>
          <Group>
            <Tooltip label={intlMessages.closeChat}>
              <IconButton
                icon={CloseIcon}
                variant="secondary"
                onClick={handleCloseChat}
                aria-label={intlMessages.closeChat}
              />
            </Tooltip>
          </Group>
        </ChatHeader>
        <MessagesPane>
          {messages.length === 0 && !loading && (
            <EmptyStateContainer>
              <EmptyStateBottomContent>
                <AiGradients />
                <AnimatedSparkleIcon
                  key="stable-animation"
                  width={48}
                  height={48}
                  triggerAnimation={isChatOpen}
                />
                <GradientText typography="headline">
                  {intlMessages.emptyStateStartConversation}
                </GradientText>
              </EmptyStateBottomContent>
            </EmptyStateContainer>
          )}
          <MessagesArea>
            {messages.map((msg, index) => (
              <ChatMessage
                key={`${msg.content}-${index}`}
                text={msg.content}
                sender={msg.role === Role.Assistant ? 'ai' : 'user'}
                feedback={msg.feedback}
                comment={msg.comment}
                messageIndex={msg.originalIndex}
                onThumbsUp={() => handleThumbsUp(msg.originalIndex)}
                onThumbsDown={() => handleThumbsDown(msg.originalIndex)}
              />
            ))}
            {loading && (
              <LoadingContainer>
                <AnimatedSparkleIcon
                  width={20}
                  height={20}
                  animationType="thinking"
                  triggerAnimation={loading}
                  data-testid="sparkle-icon"
                />
                <Text typography="accentRegular" color="systemGrayscale60">
                  {intlMessages.thinking}
                </Text>
              </LoadingContainer>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>
        </MessagesPane>
        {!isAgentPowered && <Alert variant="warning">{intlMessages.notPoweredAlert}</Alert>}
        <InputArea onSubmit={handleSendMessage}>
          {messages.length === 0 && !loading && (
            <QuickActionArea>
              <QuickActionsLabel>{intlMessages.quickActionsLabel}</QuickActionsLabel>
              {registeredPrompts.length > 0 ? (
                registeredPrompts.map(prompt => (
                  <AiButtonSendMessage key={prompt.key} message={prompt.message} />
                ))
              ) : (
                <AiButtonSendMessage message={intlMessages.quickActionsWhatCanYouDo} />
              )}
            </QuickActionArea>
          )}
          <StyledTextArea
            ref={textFieldRef}
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={intlMessages.inputPlaceholder}
            aria-label={intlMessages.inputAriaLabel}
          />
          <ButtonContainer>
            <IconButtonInside
              icon={ArrowUpIcon}
              variant="primary"
              disabled={!textFieldRef.current?.value || loading}
              type="submit"
              aria-label={intlMessages.sendButtonAriaLabel}
            />
          </ButtonContainer>
        </InputArea>
      </ChatBarContainer>

      {/* Global Comment Modal - rendered outside ChatBarContainer */}
      <CommentModal
        isOpen={modalState.isOpen}
        onClose={closeCommentModal}
        onSave={saveComment}
        initialComment={modalState.initialComment}
      />
    </>
  )
}
